import { useState, useEffect } from "react";
import axios from "axios";
import SewaClientDetail from "./SewaClientDetail";
import AirClientDetail from "./AirClientDetail";
import { useAuth } from "../../Auths/CheckUser";

const UnitSearch = () => {
  const { user } = useAuth()
  const [customerType, setCustomerType] = useState("");
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [kawasanList, setKawasanList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [selectedKawasan, setSelectedKawasan] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Fetch user data once
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;
      setLoading(true);
      try {
        const res = await axios.get(`https://bpohasil.ngrok.app/api/user/${user.id}`);
        setUserData(res.data);
      } catch (err) {
        console.error("Error fetching user data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [user]);

  // Handle customer type change
  const handleSelectChange = (e) => {
    const type = e.target.value;
    setCustomerType(type);
    setFilteredCustomers([]);
    setKawasanList([]);
    setUnitList([]);
    setSelectedKawasan("");
    setSelectedUnit("");
    setStatusFilter("");

    if (!userData) return;

    let data = [];
    if (type === "Sewa") data = userData.clients || [];
    else if (type === "Air") data = userData.airclients || [];

    if (data.length > 0) {
      const kawasanKey = type === "Sewa" ? "rentalArea" : "areaName";
      const uniqueKawasan = [...new Set(data.map((c) => c[kawasanKey]))];
      const uniqueUnits = [...new Set(data.map((c) => c.unitNumber))];

      setCustomers(data);
      setFilteredCustomers(data);
      setKawasanList(uniqueKawasan);
      setUnitList(uniqueUnits);
    }
  };

  // Filtering logic
  const applyFilters = (kawasan, unit, status) => {
    const kawasanKey = customerType === "Sewa" ? "rentalArea" : "areaName";
    let filtered = [...customers];

    if (kawasan) filtered = filtered.filter((c) => c[kawasanKey] === kawasan);
    if (unit) filtered = filtered.filter((c) => c.unitNumber === unit);
    if (status === "aktif")
      filtered = filtered.filter((c) => c.status.toLowerCase() === "aktif");
    else if (status === "tidak aktif")
      filtered = filtered.filter((c) => c.status.toLowerCase() !== "aktif");

    setFilteredCustomers(filtered);
  };

  // Handlers for filters
  const handleKawasanChange = (e) => {
    const kawasan = e.target.value;
    setSelectedKawasan(kawasan);
    applyFilters(kawasan, selectedUnit, statusFilter);
  };

  const handleUnitChange = (e) => {
    const unit = e.target.value;
    setSelectedUnit(unit);
    applyFilters(selectedKawasan, unit, statusFilter);
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setStatusFilter(status);
    applyFilters(selectedKawasan, selectedUnit, status);
  };

  return (
    <div className="text-start">
      <h3 className="mb-2 font-semibold text-lg">Pilih Customer</h3>

      {/* Customer Type */}
      <select
        className="border-2 rounded-lg w-full py-2 px-3 mb-4"
        onChange={handleSelectChange}
        value={customerType}
        disabled={loading || !userData}
      >
        <option value="">Select Customer type</option>
        <option value="Sewa">Sewa</option>
        <option value="Air">Air</option>
      </select>

      {/* Kawasan */}
      <h3 className="my-2">Pilih Kawasan</h3>
      <select
        className="border-2 rounded-lg w-full py-2 px-3"
        onChange={handleKawasanChange}
        value={selectedKawasan}
        disabled={!customerType || kawasanList.length === 0}
      >
        <option value="">All Kawasan</option>
        {kawasanList.map((area, index) => (
          <option key={index} value={area}>
            {area}
          </option>
        ))}
      </select>

      {/* Unit */}
      <h3 className="my-2">Pilih Unit</h3>
      <select
        className="border-2 rounded-lg w-full py-2 px-3"
        onChange={handleUnitChange}
        value={selectedUnit}
        disabled={!customerType || unitList.length === 0}
      >
        <option value="">All Units</option>
        {unitList.map((unit, index) => (
          <option key={index} value={unit}>
            {unit}
          </option>
        ))}
      </select>

      {/* Status Filter */}
      <h3 className="my-2">Pilih Status</h3>
      <select
        className="border-2 rounded-lg w-full py-2 px-3"
        onChange={handleStatusChange}
        value={statusFilter}
        disabled={!customerType}
      >
        <option value="">All</option>
        <option value="aktif">Aktif</option>
        <option value="tidak aktif">Tidak Aktif</option>
      </select>

      {/* Loading */}
      {loading && <p className="text-gray-600 mt-3">Loading data...</p>}

      {/* Results */}
      {!loading && filteredCustomers.length > 0 && (
        <div className="mt-3 border rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gray-100 p-2 flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              {customerType} Customers ({filteredCustomers.length})
            </h3>
          </div>

          {filteredCustomers.map((cust, index) => (
            <div
              key={index}
              className="border-t p-3 hover:bg-blue-50 cursor-pointer transition-all"
              onClick={() => setSelectedCustomer(cust)}
            >
              <p className="font-semibold">{cust.name}</p>
              <div className="grid grid-cols-2 gap-1 text-sm mt-1">
                <p className="text-gray-600">No Akaun</p>
                <p>{cust.accountNumber || "-"}</p>
                <p className="text-gray-600">Status</p>
                <p
                  className={`font-semibold ${
                    cust.status?.toLowerCase() === "aktif"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {cust.status || "-"}
                </p>
                <p className="text-gray-600">No Unit</p>
                <p>{cust.unitNumber || "-"}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && customerType && filteredCustomers.length === 0 && (
        <p className="text-gray-500 mt-3">No customers found.</p>
      )}

      {/* Popup Modals */}
      {selectedCustomer && customerType === "Sewa" && (
        <SewaClientDetail
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {selectedCustomer && customerType === "Air" && (
        <AirClientDetail
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};

export default UnitSearch;
